/**
 * 指定 timestamp 範囲の Action を高速取得する最適化クエリ。
 * 1) timestamp インデックスで action を LIMIT 付き取得
 * 2) 取得 actionId 群に対し transfers (+ crypto/nft) を一括取得
 * 3) アプリ層でグルーピング & パース
 */
import type {
  ActionCrossType,
  ActionType,
  FiatCurrency,
  TransferDirection,
} from '@0xtorch/core'
import type { Action } from '@pkg/basic'
import { inArray, sql } from 'drizzle-orm'
import { parseToAction } from '../../parsers/action'
import type { ActionTransferData } from '../../parsers/actionTransfer'
import {
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  nftTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'

/**
 * 指定期間の会計取引生成処理用 Action data を取得する
 * - 会計取引生成に不使用なデータは null や dummy で代用して DB からの転送データ量を削減する
 */
export const queryActionsForGenerateAccountingTransactionByRange =
  async (params: {
    database: DatabaseWithTransaction
    fiats: readonly FiatCurrency[]
    from: number
    to: number
    limit: number
  }): Promise<Action[]> => {
    const {
      database: { database },
      fiats,
      from,
      to,
      limit,
    } = params
    if (limit <= 0) return []

    // action + action transfer 取得
    const rows = await database.all<
      [
        string, // id
        string, // sourceId
        number, // order
        ActionType, // type
        number, // timestamp
        string | null, // loanId
        string | null, // crossId
        ActionCrossType | null, // crossType
        string | null, // targetId
        number | null, // transfer.order
        TransferDirection | null, // transfer.direction
        string | null, // transfer.cryptoId
        string | null, // transfer.fiatId
        string | null, // transfer.nftId
        string | null, // transfer.amount
        string | null, // transfer.price
      ]
    >(sql`SELECT
  ${actionTable.id},
  ${actionTable.sourceId},
  ${actionTable.order},
  ${actionTable.type},
  ${actionTable.timestamp},
  ${actionTable.loanId},
  ${actionTable.crossId},
  ${actionTable.crossType},
  ${actionTable.targetId},
  ${actionTransferTable.order},
  ${actionTransferTable.direction},
  ${actionTransferTable.cryptoId},
  ${actionTransferTable.fiatId},
  ${actionTransferTable.nftId},
  ${actionTransferTable.amount},
  ${actionTransferTable.price}
FROM (
  SELECT *
  FROM ${actionTable}
  WHERE
    ${actionTable.timestamp} >= ${from}
    AND ${actionTable.timestamp} <= ${to}
  ORDER BY
    ${actionTable.timestamp} ASC,
    ${actionTable.id} ASC
  LIMIT ${limit}
) AS ${actionTable}
LEFT JOIN ${actionTransferTable}
ON ${actionTable.id} = ${actionTransferTable.actionId};`)

    const actionRows: (typeof actionTable.$inferSelect)[] = []
    const actionIds = new Set<string>()
    const transferRows: (typeof actionTransferTable.$inferSelect)[] = []
    for (const [
      id,
      sourceId,
      order,
      type,
      timestamp,
      loanId,
      crossId,
      crossType,
      targetId,
      transferOrder,
      transferDirection,
      transferCryptoId,
      transferFiatId,
      transferNftId,
      transferAmount,
      transferPrice,
    ] of rows) {
      if (!actionIds.has(id)) {
        actionIds.add(id)
        actionRows.push({
          id,
          sourceId,
          order,
          type,
          timestamp: new Date(timestamp),
          loanId,
          crossId,
          crossType,
          targetId,
          evidence: 'none',
          comment: null,
          app: null,
          locked: false,
          generatedTx: false,
          relatedAssetIds: null,
          fromAddresses: null,
          toAddresses: null,
          hasUnknownPriceTx: null,
          hasInsufficientAmountTx: null,
          apiId: null,
          csvId: null,
        })
      }
      if (
        transferOrder !== null &&
        transferDirection !== null &&
        transferAmount !== null
      ) {
        transferRows.push({
          actionId: id,
          order: transferOrder,
          direction: transferDirection,
          from: null,
          to: null,
          cryptoId: transferCryptoId,
          fiatId: transferFiatId,
          nftId: transferNftId,
          amount: transferAmount,
          price: transferPrice,
        })
      }
    }

    // 2. nft 取得
    const nftId = new Set<string>()
    for (const row of actionRows) {
      if (row.targetId) {
        nftId.add(row.targetId)
      }
    }
    for (const row of transferRows) {
      if (row.nftId) {
        nftId.add(row.nftId)
      }
    }
    const nftRows = await database
      .select()
      .from(nftTable)
      .where(inArray(nftTable.id, Array.from(nftId)))

    // 3. crypto 取得
    const cryptoId = new Set<string>()
    for (const row of transferRows) {
      if (row.cryptoId) {
        cryptoId.add(row.cryptoId)
      }
    }
    const cryptoRows = await database
      .select()
      .from(cryptoCurrencyTable)
      .where(inArray(cryptoCurrencyTable.id, Array.from(cryptoId)))

    // 4. グルーピング
    const nftMap = new Map<string, typeof nftTable.$inferSelect>()
    for (const row of nftRows) {
      nftMap.set(row.id, row)
    }
    const cryptoMap = new Map<string, typeof cryptoCurrencyTable.$inferSelect>()
    for (const row of cryptoRows) {
      cryptoMap.set(row.id, row)
    }
    const transferMap = new Map<string, ActionTransferData[]>()
    for (const t of transferRows) {
      const list = transferMap.get(t.actionId) ?? []
      list.push({
        ...t,
        cryptoCurrencyTable: t.cryptoId
          ? (cryptoMap.get(t.cryptoId) ?? null)
          : null,
        nftTable: t.nftId ? (nftMap.get(t.nftId) ?? null) : null,
      })
      transferMap.set(t.actionId, list)
    }
    return actionRows.map((row) =>
      parseToAction(
        {
          ...row,
          // dummy action data
          evidence: 'none',
          comment: null,
          app: null,
          locked: false,
          generatedTx: false,
          relatedAssetIds: null,
          fromAddresses: null,
          toAddresses: null,
          hasUnknownPriceTx: null,
          hasInsufficientAmountTx: null,
          apiId: null,
          csvId: null,
          nftTable: row.targetId ? (nftMap.get(row.targetId) ?? null) : null,
          actionTransferTable: (transferMap.get(row.id) ?? []).sort(
            (a, b) => a.order - b.order,
          ),
        },
        fiats,
      ),
    )
  }
